import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { TranslocoService } from "@jsverse/transloco";

interface Data {
  prompt: string;
}

export interface FromScratchFormResult {
  success: boolean;
  data?: Data;
  errors?: ValidationError[];
}

export interface ValidationError {
  field: string;
  message: string;
}

@Component({
  selector: "app-from-scratch-form",
  templateUrl: "./from-scratch-form.component.html",
  styleUrl: "./from-scratch-form.component.scss",
})
export class FromScratchFormComponent implements OnInit {
  public form: FormGroup;

  @Input() disabled: boolean = false;

  @Output() formSubmit = new EventEmitter<FromScratchFormResult>();
  @Output() formChange = new EventEmitter<boolean>();

  constructor(
    private _translocoService: TranslocoService,
    private fb: FormBuilder,
  ) {}

  ngOnInit() {
    this.form = this.fb.group({
      prompt: ["", [Validators.required, this.noWhitespaceValidator]],
    });

    this.form.valueChanges.subscribe(() => {
      this.formChange.emit(this.isFormValid);
    });
  }

  private noWhitespaceValidator(control: HTMLInputElement) {
    if (!control.value) {
      return { required: true };
    }
    const isWhitespace = (control.value || "").trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : { whitespace: true };
  }

  get promptControl() {
    return this.form.get("prompt");
  }

  get isPromptInvalid() {
    return this.promptControl?.invalid && this.promptControl?.touched;
  }

  get isFormValid(): boolean {
    return this.form.valid;
  }

  get promptErrorMessage(): string {
    if (this.promptControl?.errors?.["required"]) {
      return this._translocoService.translate(
        "Prompt required error (from scratch template) - Prompt is required",
      );
    }
    if (this.promptControl?.errors?.["whitespace"]) {
      return this._translocoService.translate(
        "Prompt whitespace error (from scratch template) - Prompt cannot be empty or contain only spaces",
      );
    }
    return "";
  }

  onPromptBlur() {
    const currentValue = this.promptControl?.value;
    if (currentValue) {
      const trimmedValue = currentValue.trim();
      this.promptControl?.setValue(trimmedValue);
    }
  }

  public submitForm(): void {
    if (this.disabled) {
      return;
    }

    const validationErrors = this.validateBeforeSubmit();
    if (validationErrors.length > 0) {
      this.formSubmit.emit({
        success: false,
        errors: validationErrors,
      });
      return;
    }

    const formData = this.getFormData();
    if (formData) {
      this.formSubmit.emit({
        success: true,
        data: formData,
      });
    }
  }

  public resetForm(): void {
    this.form.reset();
  }

  public getFormData(): Data | null {
    if (!this.isFormValid) {
      return null;
    }
    return {
      prompt: this.form.get("prompt")?.value?.trim() || "",
    };
  }

  private validateBeforeSubmit(): ValidationError[] {
    const errors: ValidationError[] = [];

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      Object.keys(this.form.controls).forEach((key) => {
        const control = this.form.get(key);
        if (control?.invalid) {
          errors.push({
            field: key,
            message: `${key} is required`,
          });
        }
      });
    }

    return errors;
  }
}
